import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import {
  findNextSubQuest,
  buildSubQuestNavigationUrl,
  isAutoPlayEnabled,
} from 'utils/subQuestNavigation';

const useSubQuestAutoPlay = ({
  courseId,
  questId,
  subQuestId,
  classId = null,
  quests = [],
}) => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const toastIdRef = useRef(null);
  const isAutoPlayActiveRef = useRef(false);
  const toast = useToast();
  const [countdown, setCountdown] = useState(0);

  const progressions = useSelector(
    (state) => state.progression?.progressions || []
  );

  const isCurrentSubQuestCompleted = useCallback(() => {
    return progressions.some(
      (p) =>
        p.questId === questId &&
        p.courseId === courseId &&
        p.subQuestId === subQuestId &&
        p.dateFinished
    );
  }, [progressions, questId, courseId, subQuestId]);

  const getNextSubQuest = useCallback(() => {
    const questsWithSubQuests = quests.filter(
      (quest) =>
        quest.subQuests &&
        Array.isArray(quest.subQuests) &&
        quest.subQuests.length > 0
    );

    return findNextSubQuest(questsWithSubQuests, questId, subQuestId);
  }, [quests, questId, subQuestId]);

  const showCountdownToast = useCallback(
    (nextSubQuest) => {
      // Prevent multiple toasts
      if (isAutoPlayActiveRef.current) {
        return;
      }

      isAutoPlayActiveRef.current = true;
      setCountdown(3);

      toastIdRef.current = toast({
        title: `Auto-advancing in 3 seconds...`,
        description: `Next: ${nextSubQuest.subQuest.name}`,
        status: 'info',
        duration: 3000,
        isClosable: false,
        position: 'top-right',
      });

      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = prev - 1;

          // Update toast with new countdown
          if (toastIdRef.current) {
            toast.update(toastIdRef.current, {
              title: `Auto-advancing in ${newCount} seconds...`,
              description: `Next: ${nextSubQuest.subQuest.name}`,
              status: 'info',
              position: 'top-right',
            });
          }

          return newCount;
        });
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        const navigationUrl = buildSubQuestNavigationUrl(
          courseId,
          nextSubQuest.questId,
          nextSubQuest.subQuestId,
          classId
        );
        navigate(navigationUrl);
        setCountdown(0);
        isAutoPlayActiveRef.current = false;

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, 3000);
    },
    [courseId, classId, navigate, toast]
  );

  const navigateToNextSubQuest = useCallback(() => {
    const nextSubQuest = getNextSubQuest();
    if (nextSubQuest) {
      const navigationUrl = buildSubQuestNavigationUrl(
        courseId,
        nextSubQuest.questId,
        nextSubQuest.subQuestId,
        classId
      );

      toast({
        title: 'Auto-advancing to next activity',
        description: `Moving to: ${nextSubQuest.subQuest.name}`,
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      navigate(navigationUrl);
    } else {
      toast({
        title: 'Quest completed!',
        description: 'You have completed all activities in this quest.',
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }, [getNextSubQuest, courseId, classId, navigate, toast]);

  const triggerAutoPlay = useCallback(() => {
    if (!isAutoPlayEnabled()) {
      return;
    }

    if (isAutoPlayActiveRef.current) {
      return;
    }

    const nextSubQuest = getNextSubQuest();
    if (!nextSubQuest) {
      return;
    }

    // Clear any existing timeout and interval
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    showCountdownToast(nextSubQuest);
  }, [getNextSubQuest, showCountdownToast]);

  const cancelAutoPlay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
      toastIdRef.current = null;
    }
    setCountdown(0);
    isAutoPlayActiveRef.current = false;
  }, [toast]);

  const hasNextSubQuest = useCallback(() => {
    return getNextSubQuest() !== null;
  }, [getNextSubQuest]);

  const manualNavigateToNext = useCallback(() => {
    cancelAutoPlay();
    navigateToNextSubQuest();
  }, [cancelAutoPlay, navigateToNextSubQuest]);

  // Cleanup timeout and interval on unmount or when subQuestId changes
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      isAutoPlayActiveRef.current = false;
    };
  }, [subQuestId]);

  return {
    triggerAutoPlay,
    cancelAutoPlay,
    navigateToNextSubQuest: manualNavigateToNext,
    hasNextSubQuest: hasNextSubQuest(),
    isCurrentSubQuestCompleted: isCurrentSubQuestCompleted(),
    getNextSubQuest,
    countdown,
  };
};

export default useSubQuestAutoPlay;
